/* eslint-disable filename-rules/match */
import { overlay } from "overlay-kit";

import { ModelType } from "./type-utils";

import { serverCopyActivity, serverMoveActivityWithinSection } from "api/api-server-actions";
import {
  ActivityCategory,
  ActivityType,
  LessonOrArchiveActivityCategory,
} from "components/server-types";
import { t } from "i18n/i18n";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { store, storeApi } from "store/index";
import { selectPagesByActivityId } from "store/selectors";
import {
  toastLocalizedOperationCatcher,
  toastMoveOperationCatcher,
  toastSuccessMessage,
} from "utils/alerts";
import { refreshTopicPageCounts } from "utils/topic";

export type ReleaseStatus = "released" | "unreleased" | "partiallyReleased" | null;

export const moveActivityWithinSection = (itemId: string, destinationTopicId: string) => {
  const destinationKey = "topic";
  serverMoveActivityWithinSection(itemId, destinationTopicId)
    .then(() => {
      toastSuccessMessage(
        t("content_view.item_moved", { destinationCategory: t(`glossary.${destinationKey}`) }),
      );
    })
    .catch(toastMoveOperationCatcher(destinationKey));
};

export const copyActivity = (activityId: string) => {
  serverCopyActivity(activityId).catch(
    toastLocalizedOperationCatcher("failed_to_duplicate_activity"),
  );
};

export const isArchived = (category: ActivityCategory) => category === ActivityCategory.ARCHIVE;

export const isLesson = (category: ActivityCategory) => category === ActivityCategory.LESSON;

export const isPrep = (category: ActivityCategory) => category === ActivityCategory.PREP;

export const isAdditionalResource = (category: ActivityCategory) =>
  category === ActivityCategory.ADDITIONAL;

const createActivity = (category: ActivityCategory, topicId: string, sectionId: string) => {
  if (topicId === null) {
    // TODO: raise an error?
    return;
  }

  return storeApi.activities.create({
    title: t("glossary.activity"),
    topic_id: topicId,
    section_id: sectionId,
    category,
    estimated_time: undefined,
  });
};

const saveActivity = (activity: Partial<ActivityType> & { id: string }) =>
  storeApi.activities.partial_update(activity);

const confirmArchiveActivity = () =>
  overlay.openAsync<boolean>(({ isOpen, close }) => (
    <ConfirmationDialog
      confirmButtonText={t("archive_activity_modal.confirm")}
      description={t("archive_activity_modal.description")}
      open={isOpen}
      title={t("archive_activity_modal.title")}
      isWarningDescription
      onClose={close}
      onConfirm={() => {}}
    />
  ));

const unpublishAllPagesForActivity = async (activity: Pick<ActivityType, "id" | "topic_id">) => {
  const pages = selectPagesByActivityId(store.getState(), activity.id);
  await Promise.all(
    pages.map((page) =>
      // TODO: We currently don't have a way of doing this in bulk. Also, it might be better
      // to handle this on the backend.
      storeApi.pages.partial_update({
        id: page.id,
        activity_id: activity.id,
        released_at: null,
      }),
    ),
  );

  await refreshTopicPageCounts(activity.topic_id);
};

// Changes the activity type between `lesson` or `archive`, presenting alerts if necessary. Not for use
// with drag and drop, which modifies the activity locally during the drag operation.
const onActivityTypeChanged = async (
  activity: Pick<ActivityType, "id" | "category" | "topic_id">,
  newCategory: LessonOrArchiveActivityCategory,
) => {
  if (activity.category === newCategory) {
    return false;
  }

  // Pages are unpublished on archive. We confirm with the user before unpublishing.
  const pages = selectPagesByActivityId(store.getState(), activity.id);
  if (isArchived(newCategory) && pages.some((page) => page.released_at)) {
    const confirm = await confirmArchiveActivity();
    if (!confirm) {
      return;
    }

    await unpublishAllPagesForActivity(activity);
  }

  return saveActivity({ id: activity.id, category: newCategory });
};

const deleteActivity = (activity: ModelType) => storeApi.activities.destroy(activity.id);

const archiveActivity = (activity: Pick<ActivityType, "id" | "category" | "topic_id">) =>
  onActivityTypeChanged(activity, ActivityCategory.ARCHIVE);

const unarchiveActivity = (activity: Pick<ActivityType, "id" | "category" | "topic_id">) =>
  onActivityTypeChanged(activity, ActivityCategory.LESSON);

const dropActivity = async (
  activity: Pick<ActivityType, "id" | "category" | "order" | "topic_id">,
) => {
  // Pages are unpublished on archive. We confirm with the user before unpublishing.
  const pages = selectPagesByActivityId(store.getState(), activity.id);
  if (isArchived(activity.category) && pages.some((page) => page.released_at)) {
    const confirm = await confirmArchiveActivity();
    if (!confirm) {
      return;
    }

    await unpublishAllPagesForActivity(activity);
  }

  return saveActivity({ id: activity.id, category: activity.category, order: activity.order });
};

/*
 * The following hook returns functions for maintaining activities in the store.
 */
export const useActivityActions = () => {
  return {
    createActivity,
    saveActivity,
    deleteActivity,
    dropActivity,
    archiveActivity,
    unarchiveActivity,
  };
};
